import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import type { GetStaticProps } from "next";
import Head from "next/head";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import { Footer } from "components/modules/Layout/Footer/Footer";
import { BetaBanner } from "components/common/BetaBanner/BetaBanner";
import { capitalize } from "client/utils/capitalize";
import { translateFunc } from "client/utils/translateFunc";
import { commonLanguageFiles } from "client/config";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { Translate } from "components/common/Translate/Translate";
import {TrustPilotSection} from "components/modules/TrustPilot/TrustPilotSection";
import React from "react";

const AboutPage = () => {
    return (
        <>
            <Head>
                <title>{`${capitalize(translateFunc("about-us"))} | ImageResizer.com`}</title>
            </Head>
            <div className="max-w-1440 page-shadow text-black dark:text-white dark:bg-darkSurface-100">
                <Navbar url="/about" />
                <div className="py-8 mx-8 md:mx-24 lg:mx-44 flex flex-col gap-8 leading-relaxed">
                    <BetaBanner />
                    <h1 className="text-3xl font-semibold capitalize">
                        <Translate keyName="about-us" />
                    </h1>
                    <div>
                        <Translate keyName="about-us-line-1" />
                    </div>
                    <div>
                        <Translate keyName="about-us-line-2" />
                    </div>
                    <div>
                        <Translate keyName="about-us-line-3" />
                    </div>
                    <div className="text-2xl text-[#555555] dark:text-white font-bold capitalize">
                        <Translate keyName="more-info" />:
                    </div>
                    <ul className="flex flex-col gap-4 ml-8 list-disc">
                        <li>{`${translateFunc("team-email")}: ImageResizer.com@gmail.com`}</li>
                        <li>
                            <Translate keyName="team-location" />
                        </li>
                        <li>{`${translateFunc("team-members")}: 4`}</li>
                        <li>{`TRMedia Inc. 1 Dundas Street West, Toronto, ON, Canada (Unit #2554)`}</li>
                        <li>
                            <Translate keyName="technology-stack" />
                            {`: `}
                            <a href="https://reactjs.org/" target={"_blank"} className="text-irBlue-600">
                                ReactJS
                            </a>
                            {`, `}
                            <a href="https://nextjs.org/" target={"_blank"} className="text-irBlue-600">
                                NextJS
                            </a>
                            {`, `}
                            <a href="https://tailwindcss.com/" target={"_blank"} className="text-irBlue-600">
                                Tailwind CSS
                            </a>
                            {`, `}
                            <a href="https://www.mongodb.com/" target={"_blank"} className="text-irBlue-600">
                                MongoDB
                            </a>
                            {`, ${translateFunc("hosted-on-aws")}, `}
                            <Translate
                                keyName="managed-using-jira"
                                linkRenderer={(_, key) => (
                                    <a
                                        href="https://www.atlassian.com/software/jira"
                                        target={"_blank"}
                                        className="text-irBlue-600"
                                        key={key}
                                    >
                                        JIRA
                                    </a>
                                )}
                            />
                        </li>
                    </ul>
                    <div>
                        <Translate keyName="thanks-text" />
                    </div>
                </div>
                <TrustPilotSection />
                <Footer url="/about" />
            </div>
        </>
    );
};

const files = ["about", ...commonLanguageFiles];

const LanguageWrapper = () => (
    <LanguageProvider files={files}>
        <AboutPage />
    </LanguageProvider>
);

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        ...(await serverSideTranslations(locale ?? "", files)),
    },
});

export default LanguageWrapper;
